import React, { useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Box, Container } from '@mui/material';

import SectionNavigation from '~/components/Common/Elements/SectionNavigation';
import SectionNavigationLayout from '~/components/Common/Elements/SectionNavigationLayout';
import { preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';

import PageHeader from '../Common/Elements/PageHeader';
import EmployeeActivationEmailContainer from '../EmployeeActivationEmail/EmployeeActivationEmailContainer';
import FailedEmailContainer from '../FailedEmail/FailedEmail';

interface TabConfig {
  label: string;
  component: React.ComponentType;
  tenants?: Set<string>;
}

const tabsMap: Record<string, TabConfig> = {
  'failed-email': {
    label: 'Failed Email',
    component: FailedEmailContainer,
  },
  'activation-emails': {
    label: 'Employee Activation Emails',
    component: EmployeeActivationEmailContainer,
    tenants: new Set(['lebusinesstraveller']),
  },
};

type TabType = keyof Partial<typeof tabsMap> | undefined;

preRegisterSpotlightRoutes(
  Object.entries(tabsMap).map(([key, tab]) => ({
    path: `/email/${key}`,
    label: `Email / ${tab.label}`,
    section: 'Email',
  })),
);

export default function EmailPage() {
  const { tab } = useParams<{ tab: TabType }>();
  const key = tab || 'failed-email';
  const currentTab = tabsMap[key];

  const brand = useSelector((state: App.State) => state.tenant);

  const tabsList = useMemo(() => {
    return Object.keys(tabsMap).filter((key) => !tabsMap[key].tenants || tabsMap[key].tenants.has(brand.value));
  }, [brand]);

  const title = `Email / ${currentTab.label}`;
  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <Container maxWidth="xl">
        <PageHeader title={title} />

        <SectionNavigationLayout
          navigation={
            <SectionNavigation
              label="Email sections"
              currentTab={key}
              groups={[
                {
                  items: tabsList.map((id) => ({ id, label: tabsMap[id].label, href: `/email/${id}` })),
                },
              ]}
            />
          }
        >
          <Box component={currentTab.component} />
        </SectionNavigationLayout>
      </Container>
    </>
  );
}
